import React from 'react';
import { Moon, Sun } from '@phosphor-icons/react';
import { Icon } from '../Icon';
import { Glyph } from './glyphs';
import { useTheme } from '../../../Contexts/ThemeContext';
import { cn } from '../../lib/cn';

/**
 * Theme toggle, wired to the real ThemeContext.
 *
 * The app already owns the theme: `ThemeProvider` writes `.dark-theme` on
 * <html> and every design-system layer flips off that one class. So this is
 * only a control, it holds no state of its own and invents none.
 *
 * Two placements, because the two shells with chrome have a bar to put it in
 * and the two without do not:
 *
 *   `variant="bar"`      an icon button that lives inside the top nav or the
 *                        utility bar (`.dsk-iconbtn` / `.ds-iconbtn--bare`)
 *   `variant="floating"` a fixed circle, bottom right, for AuthShell and
 *                        StatusShell which carry no chrome at all
 *
 * The icon is never filled. Fill means active, and neither sun nor moon is a
 * state of the button: they are labels for what the click will DO.
 *
 * @param {{ track?: 'desktop'|'mobile', variant?: 'bar'|'floating', className?: string }} props
 */
export function ThemeToggle({ track = 'desktop', variant = 'bar', className }) {
  const { isDarkMode, toggleTheme } = useTheme();
  const isDesktop = track === 'desktop';
  const prefix = isDesktop ? 'dsk' : 'ds';

  const label = isDarkMode ? 'Switch to light theme' : 'Switch to dark theme';

  const barClass = isDesktop ? 'dsk-iconbtn' : 'ds-iconbtn ds-iconbtn--bare';
  const rootClass =
    variant === 'floating' ? `${prefix}-shell-themetoggle` : barClass;

  return (
    <button
      type="button"
      className={cn(rootClass, className)}
      onClick={toggleTheme}
      aria-label={label}
      title={label}
    >
      {isDesktop ? (
        <Icon as={isDarkMode ? Sun : Moon} />
      ) : (
        <Glyph name={isDarkMode ? 'sun' : 'moon'} />
      )}
    </button>
  );
}

export default ThemeToggle;
